import { createRoute } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Settings, TestTube, Save, RotateCcw, Eye, EyeOff } from "lucide-react";
import { rootRoute } from "./__root";
import { api } from "../lib/api";
import { useAuth } from "../lib/use-auth";
import styles from "./AdminSettings.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/admin/settings",
	component: withPermission(AdminSettingsPage, "admin", "read"),
});

interface SettingRow {
	key: string;
	value: string | null;
	valueI18n: Record<string, string> | null;
	category: string;
	isSecret: boolean;
	description: string | null;
	updatedAt: string | null;
}

interface TestResult {
	type: "success" | "error";
	message: string;
}

function AdminSettingsPage() {
	const { t } = useTranslation();
	const { user } = useAuth();
	const [settings, setSettings] = useState<SettingRow[]>([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [testResults, setTestResults] = useState<Record<string, TestResult>>({});
	const [testing, setTesting] = useState<Record<string, boolean>>({});
	const [revealed, setRevealed] = useState<Record<string, boolean>>({});
	const [revealing, setRevealing] = useState<Record<string, boolean>>({});
	const [saveStatus, setSaveStatus] = useState<TestResult | null>(null);
	const [termsLang, setTermsLang] = useState<"tr" | "en">("tr");

	useEffect(() => {
		if (user && user.role !== "admin") {
			window.location.href = "/";
		}
	}, [user]);

	useEffect(() => {
		let cancelled = false;
		api
			.get<SettingRow[]>("/api/admin/settings")
			.then((rows) => {
				if (!cancelled) {
					setSettings(rows);
					setLoading(false);
				}
			})
			.catch(() => {
				if (!cancelled) {
					setSettings([]);
					setLoading(false);
				}
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const updateValue = (key: string, value: string | null) => {
		setSettings((prev) => prev.map((s) => (s.key === key ? { ...s, value } : s)));
		setSaveStatus(null);
	};

	const updateTermValue = (key: string, lang: "tr" | "en", value: string) => {
		setSettings((prev) =>
			prev.map((s) =>
				s.key === key ? { ...s, valueI18n: { ...(s.valueI18n ?? {}), [lang]: value } } : s,
			),
		);
		setSaveStatus(null);
	};

	const handleSave = async () => {
		setSaving(true);
		setSaveStatus(null);
		try {
			await api.patch(
				"/api/admin/settings",
				settings.map((s) => {
					if (s.category === "offer_terms") {
						const tr = s.valueI18n?.["tr"] ?? s.value ?? "";
						const en = s.valueI18n?.["en"] ?? "";
						return { key: s.key, value: tr || null, valueI18n: { tr, en } };
					}
					return { key: s.key, value: s.value };
				}),
			);
			setSaveStatus({ type: "success", message: t("saveSuccess") });
		} catch (err) {
			setSaveStatus({
				type: "error",
				message: err instanceof Error ? err.message : t("error"),
			});
		} finally {
			setSaving(false);
		}
	};

	const handleTest = async (endpoint: string, key: string) => {
		setTesting((prev) => ({ ...prev, [key]: true }));
		setTestResults((prev) => {
			const next = { ...prev };
			delete next[key];
			return next;
		});
		try {
			const result = await api.post<{ success: boolean; message: string }>(endpoint, {});
			setTestResults((prev) => ({ ...prev, [key]: { type: "success", message: result.message } }));
		} catch (err) {
			const message = err instanceof Error ? err.message : t("error");
			setTestResults((prev) => ({ ...prev, [key]: { type: "error", message } }));
		} finally {
			setTesting((prev) => ({ ...prev, [key]: false }));
		}
	};

	const grouped = settings.reduce<Record<string, SettingRow[]>>((acc, s) => {
		const cat = s.category;
		if (!acc[cat]) acc[cat] = [];
		acc[cat].push(s);
		return acc;
	}, {});

	const categoryOrder = ["general", "sap", "ollama", "mssql", "v1_sql", "smtp", "offer_terms"];
	const categories = categoryOrder.filter((c) => grouped[c] && grouped[c].length > 0);

	if (loading) {
		return (
			<div className={styles.container}>
				<div className={styles.heading}>{t("adminSettingsTitle")}</div>
				<div className={styles.loading}>{t("loading")}</div>
			</div>
		);
	}

	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<div className={styles.headingRow}>
					<Settings size={22} />
					<h1 className={styles.heading}>{t("adminSettingsTitle")}</h1>
				</div>
				<div className={styles.actions}>
					<button
						type="button"
						className={styles.secondaryButton}
						onClick={() => window.location.reload()}
						disabled={saving}
						title={t("reset")}
					>
						<RotateCcw size={14} />
						{t("reset")}
					</button>
					<button
						type="button"
						className={styles.primaryButton}
						onClick={handleSave}
						disabled={saving}
					>
						<Save size={14} />
						{saving ? t("saving") : t("save")}
					</button>
				</div>
			</div>

			{saveStatus && (
				<div
					className={`${styles.statusBanner} ${saveStatus.type === "success" ? styles.statusSuccess : styles.statusError}`}
				>
					{saveStatus.message}
				</div>
			)}

			{categories.map((cat) => (
				<section key={cat} className={styles.section}>
					<h2 className={styles.sectionTitle}>{t(`category_${cat}`)}</h2>
					{cat === "offer_terms" && (
						<div className={styles.termsLangRow}>
							<button
								type="button"
								className={[styles.langButton, termsLang === "tr" && styles.langButtonActive]
									.filter(Boolean)
									.join(" ")}
								onClick={() => setTermsLang("tr")}
							>
								TR
							</button>
							<button
								type="button"
								className={[styles.langButton, termsLang === "en" && styles.langButtonActive]
									.filter(Boolean)
									.join(" ")}
								onClick={() => setTermsLang("en")}
							>
								EN
							</button>
						</div>
					)}
					<div className={styles.fields}>
						{grouped[cat]?.map((s) => (
							<div key={s.key} className={styles.field}>
								<label className={styles.label} htmlFor={s.key}>
									{s.key}
									{s.description && <span className={styles.hint}>{s.description}</span>}
								</label>
								<div className={styles.inputWrap}>
									{cat === "offer_terms" ? (
										<textarea
											id={s.key}
											className={styles.textarea}
											value={
												(termsLang === "tr"
													? (s.valueI18n?.["tr"] ?? s.value)
													: s.valueI18n?.["en"]) ?? ""
											}
											onChange={(e) => updateTermValue(s.key, termsLang, e.target.value)}
											placeholder={t("notConfigured")}
											rows={4}
										/>
									) : (
										<input
											id={s.key}
											type={s.isSecret && !revealed[s.key] ? "password" : "text"}
											className={styles.input}
											value={s.value ?? ""}
											onChange={(e) => updateValue(s.key, e.target.value || null)}
											placeholder={t("notConfigured")}
										/>
									)}
									{s.isSecret && (
										<button
											type="button"
											className={styles.revealButton}
											onClick={async () => {
												if (s.isSecret && s.value === "••••••••" && !revealed[s.key]) {
													setRevealing((prev) => ({ ...prev, [s.key]: true }));
													try {
														const result = await api.get<{
															success: boolean;
															value: string | null;
														}>(`/api/admin/settings/${s.key}/reveal`);
														updateValue(s.key, result.value);
													} catch {
														// leave mask in place on error
													} finally {
														setRevealing((prev) => ({ ...prev, [s.key]: false }));
													}
												}
												setRevealed((prev) => ({ ...prev, [s.key]: !prev[s.key] }));
											}}
											tabIndex={-1}
										>
											{revealing[s.key] ? (
												<span className={styles.spinner} />
											) : revealed[s.key] ? (
												<EyeOff size={16} />
											) : (
												<Eye size={16} />
											)}
										</button>
									)}
								</div>
							</div>
						))}
					</div>
					<div className={styles.testRow}>
						{cat === "smtp" && (
							<button
								type="button"
								className={styles.testButton}
								onClick={() => handleTest("/api/admin/settings/test-smtp", "smtp")}
								disabled={testing["smtp"]}
							>
								<TestTube size={14} />
								{testing["smtp"] ? t("testing") : t("testConnection")}
							</button>
						)}
						{cat === "sap" && (
							<button
								type="button"
								className={styles.testButton}
								onClick={() => handleTest("/api/admin/settings/test-sap", "sap")}
								disabled={testing["sap"]}
							>
								<TestTube size={14} />
								{testing["sap"] ? t("testing") : t("testConnection")}
							</button>
						)}
						{cat === "ollama" && (
							<button
								type="button"
								className={styles.testButton}
								onClick={() => handleTest("/api/admin/settings/test-ollama", "ollama")}
								disabled={testing["ollama"]}
							>
								<TestTube size={14} />
								{testing["ollama"] ? t("testing") : t("testConnection")}
							</button>
						)}
						{cat === "v1_sql" && (
							<button
								type="button"
								className={styles.testButton}
								onClick={() => handleTest("/api/admin/settings/test-v1", "v1_sql")}
								disabled={testing["v1_sql"]}
							>
								<TestTube size={14} />
								{testing["v1_sql"] ? t("testing") : t("testConnection")}
							</button>
						)}
						{testResults[cat] && (
							<span
								className={`${styles.testResult} ${testResults[cat].type === "success" ? styles.testSuccess : styles.testError}`}
							>
								{testResults[cat].message}
							</span>
						)}
					</div>
				</section>
			))}
		</div>
	);
}
